/**
 * Subscription management business logic
 * Shared between web and mobile applications
 */

import { planFromStripeInterval } from './subscriptionState';

/**
 * Normalize expiration date to milliseconds
 * Handles both seconds and milliseconds timestamps, as well as string numbers
 * @param {number|Date|string} expirationDate - Expiration date as timestamp, Date object, or string number
 * @returns {number} Expiration date in milliseconds
 */
export const normalizeExpirationDate = (expirationDate) => {
    // Handle null/undefined
    if (expirationDate == null) {
        return 0;
    }
    
    // If it's a string that represents a number, parse it first
    let numericValue = expirationDate;
    if (typeof expirationDate === 'string') {
        // Try to parse as number
        const parsed = Number(expirationDate);
        if (!isNaN(parsed) && isFinite(parsed)) {
            numericValue = parsed;
        } else {
            // If not a valid number string, try Date parsing
            numericValue = new Date(expirationDate).getTime();
        }
    } else if (expirationDate instanceof Date) {
        numericValue = expirationDate.getTime();
    } else if (typeof expirationDate === 'number') {
        numericValue = expirationDate;
    } else {
        // Fallback: try to convert via Date
        numericValue = new Date(expirationDate).getTime();
    }
    
    // If timestamp is in seconds (< 1 trillion), convert to milliseconds
    return numericValue < 1e12 ? numericValue * 1000 : numericValue;
};

/**
 * Check if subscription is currently active
 * @param {number|Date} expirationDate - Expiration date as timestamp or Date object
 * @returns {boolean} True if subscription is active
 */
export const isSubscriptionActive = (expirationDate) => {
    return normalizeExpirationDate(expirationDate) > Date.now();
};

/**
 * Check if subscription metadata is complete
 * @param {Object} subscription - Subscription object
 * @returns {boolean} True if all required metadata is present
 */
export const isSubscriptionMetadataComplete = (subscription) => {
    return subscription?.paymentMode &&
           subscription?.paymentType &&
           subscription?.plan;
};

/**
 * Convert interval to plan name (Monthly, Quarterly, Annual, etc.).
 * Delegates to the single canonical implementation in subscriptionState so the
 * two can't drift; that version also guards null/undefined `interval` (the old
 * inline `interval.toLowerCase()` here threw on null).
 * @param {string} interval - Interval type (month, year)
 * @param {number} intervalCount - Number of intervals
 * @returns {string} Plan name
 */
export const getPlanFromInterval = (interval, intervalCount) =>
    planFromStripeInterval(interval, intervalCount);

/**
 * Validate subscription expiration and get status
 * @param {Object} subscription - Subscription object with expiration date
 * @returns {Object} Status object with isActive and metadata flags
 */
export const getSubscriptionStatus = (subscription) => {
    if (!subscription?.subscription_expiration) {
        return {
            isActive: false,
            hasMetadata: false,
            needsUpdate: false
        };
    }

    const isActive = isSubscriptionActive(subscription.subscription_expiration);
    const hasMetadata = isSubscriptionMetadataComplete(subscription);

    return {
        isActive,
        hasMetadata,
        needsUpdate: isActive && !hasMetadata
    };
};
